import { z } from 'zod';
import { idSchema } from './common.js';
// -----------------------------------------------------------------------------
// Direct messages (Session 10) — schemas for the six /v1/social conversation
// routes plus the admin driver-verification route.
// -----------------------------------------------------------------------------
/** Param: a conversation ULID. */
export const conversationIdParamSchema = z.object({
    conversationId: idSchema,
});
/** Body: open (or fetch) a 1:1 DM with another user. */
export const createConversationBodySchema = z.object({
    recipientUserId: idSchema,
});
/** Query: page a thread, newest-first. Cursor is the last seq seen. */
export const listMessagesQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.coerce.number().int().positive().optional(),
});
/** Maximum number of media items on a single message (mirrors posts). */
export const MESSAGE_MEDIA_MAX_COUNT = 4;
/**
 * Media attached to a message. Client uploads bytes directly to R2 via the
 * presign flow and passes the r2Key here; the service validates the key sits
 * under the caller's own prefix before persisting. Mirrors postMediaInputSchema
 * (no altText).
 */
const messageMediaInputSchema = z.object({
    r2Key: z.string().trim().min(1).max(512),
    mimeType: z.string().trim().min(1).max(64),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    sizeBytes: z.number().int().positive(),
});
/**
 * Body: send a message. A message needs a body OR media (or both) — the old
 * messages_body_not_empty CHECK was dropped in 0013; this refine is now the
 * enforcement point, together with the service.
 */
export const sendMessageBodySchema = z
    .object({
    body: z.string().trim().max(4000).default(''),
    media: z
        .array(messageMediaInputSchema)
        .max(MESSAGE_MEDIA_MAX_COUNT)
        .optional(),
})
    .refine((v) => v.body.length > 0 || (v.media?.length ?? 0) > 0, {
    message: 'Message must have a body or at least one media item',
    path: ['body'],
});
/** Body: advance the read high-water mark to a seen seq. */
export const markReadBodySchema = z.object({
    seq: z.number().int().positive(),
});
// -----------------------------------------------------------------------------
// Admin: driver verification
// -----------------------------------------------------------------------------
/** Body: set a driver's verification state (platform_admin only). */
export const setDriverVerificationBodySchema = z.object({
    state: z.enum(['none', 'self_declared', 'pending_review', 'verified', 'rejected']),
    notes: z.string().trim().max(2000).optional(),
});
//# sourceMappingURL=conversation.js.map